import { auth } from "@lib/firebase";
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  TotpMultiFactorGenerator,
  getMultiFactorResolver,
  multiFactor,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  type MultiFactorError,
  type MultiFactorInfo,
  type MultiFactorResolver,
  type PhoneMultiFactorInfo,
  type TotpSecret,
  type UserCredential,
} from "firebase/auth";

export const MFA_REQUIRED_ERROR = "auth/multi-factor-auth-required";
export const RECENT_LOGIN_REQUIRED_ERROR = "auth/requires-recent-login";
export const UNVERIFIED_EMAIL_ERROR = "auth/unverified-email";
export const INVALID_TOTP_CODE_ERRORS: string[] = [
  "auth/invalid-verification-code",
  "auth/totp-challenge-timeout",
];
export const INVALID_SMS_CODE_ERRORS: string[] = [
  "auth/invalid-verification-code",
  "auth/code-expired",
];
export const INVALID_PHONE_NUMBER_ERROR = "auth/invalid-phone-number";
export const SMS_TOO_MANY_REQUESTS_ERROR = "auth/too-many-requests";

// Issuer shown next to the account email inside authenticator apps.
const TOTP_ISSUER = "Storydoc";

export interface TotpEnrollmentStart {
  secret: TotpSecret;
  qrCodeUrl: string;
  secretKey: string;
}

/**
 * Returns the second factors currently enrolled for the signed-in user.
 */
export const getEnrolledFactors = (): MultiFactorInfo[] => {
  const user = auth.currentUser;
  if (!user) return [];
  return multiFactor(user).enrolledFactors;
};

/**
 * Whether the signed-in user has an authenticator app (TOTP) enrolled.
 */
export const isTotpEnrolled = (): boolean =>
  getEnrolledFactors().some(
    (factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID,
  );

/**
 * Starts TOTP enrollment: Firebase generates the shared secret, and we build
 * the otpauth:// URL the user scans with their authenticator app.
 * Throws auth/requires-recent-login when the session is too old and
 * auth/unverified-email when the user's email is not verified.
 */
export const startTotpEnrollment = async (): Promise<TotpEnrollmentStart> => {
  const user = auth.currentUser;
  if (!user) throw new Error("No user is currently logged in");
  const session = await multiFactor(user).getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  const qrCodeUrl = secret.generateQrCodeUrl(user.email ?? "", TOTP_ISSUER);
  return { secret, qrCodeUrl, secretKey: secret.secretKey };
};

/**
 * Completes TOTP enrollment by verifying the first code from the user's
 * authenticator app against the pending secret.
 */
export const confirmTotpEnrollment = async (
  secret: TotpSecret,
  code: string,
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error("No user is currently logged in");
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
    secret,
    code,
  );
  await multiFactor(user).enroll(assertion, "Authenticator app");
};

/**
 * Removes the enrolled authenticator-app factor for the signed-in user.
 * Firebase requires a recent login (auth/requires-recent-login) and may
 * revoke the session afterwards.
 */
export const unenrollTotp = async (): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error("No user is currently logged in");
  const totpFactor = multiFactor(user).enrolledFactors.find(
    (factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID,
  );
  if (!totpFactor) return;
  await multiFactor(user).unenroll(totpFactor);
};

/**
 * Whether the signed-in user has an SMS (phone) second factor enrolled.
 */
export const isSmsEnrolled = (): boolean =>
  getEnrolledFactors().some(
    (factor) => factor.factorId === PhoneMultiFactorGenerator.FACTOR_ID,
  );

/**
 * Last two digits of the enrolled SMS phone number, for "ending in **NN"
 * copy. Empty string when no phone factor is enrolled.
 */
export const getEnrolledPhoneLastDigits = (): string => {
  const phoneFactor = getEnrolledFactors().find(
    (factor) => factor.factorId === PhoneMultiFactorGenerator.FACTOR_ID,
  ) as PhoneMultiFactorInfo | undefined;
  return phoneFactor?.phoneNumber?.slice(-2) ?? "";
};

/**
 * Creates the reCAPTCHA verifier Firebase requires before it will send an MFA
 * SMS, disposing of the previous one. grecaptcha refuses to
 * render twice into the same element (even after .clear(), which can leave
 * DOM artifacts behind), so every verifier gets a brand-new child node
 * inside the wrapper.
 */
export const createFreshRecaptcha = (
  wrapper: HTMLElement,
  previous?: RecaptchaVerifier | null,
  size: "invisible" | "normal" = "invisible",
): RecaptchaVerifier => {
  try {
    previous?.clear();
  } catch {
    // The widget may already be gone or in an errored state — ignore.
  }
  const mount = document.createElement("div");
  wrapper.replaceChildren(mount);
  return new RecaptchaVerifier(auth, mount, { size });
};

/**
 * Starts SMS enrollment: Firebase texts a 6-digit code to the number.
 * Returns the verificationId needed to confirm. Throws
 * auth/requires-recent-login on stale sessions and
 * auth/invalid-phone-number for malformed numbers (expects E.164).
 */
export const startSmsEnrollment = async (
  phoneNumber: string,
  recaptchaVerifier: RecaptchaVerifier,
): Promise<string> => {
  const user = auth.currentUser;
  if (!user) throw new Error("No user is currently logged in");
  const session = await multiFactor(user).getSession();
  return new PhoneAuthProvider(auth).verifyPhoneNumber(
    { phoneNumber, session },
    recaptchaVerifier,
  );
};

/**
 * Completes SMS enrollment with the code the user received.
 */
export const confirmSmsEnrollment = async (
  verificationId: string,
  code: string,
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error("No user is currently logged in");
  const credential = PhoneAuthProvider.credential(verificationId, code);
  const assertion = PhoneMultiFactorGenerator.assertion(credential);
  await multiFactor(user).enroll(assertion, "SMS");
};

/**
 * Removes the enrolled SMS factor. Same recent-login requirement as TOTP.
 */
export const unenrollSms = async (): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error("No user is currently logged in");
  const phoneFactor = multiFactor(user).enrolledFactors.find(
    (factor) => factor.factorId === PhoneMultiFactorGenerator.FACTOR_ID,
  );
  if (!phoneFactor) return;
  await multiFactor(user).unenroll(phoneFactor);
};

/**
 * Reloads the current user and force-refreshes the ID token so a
 * backend-performed email verification (Admin SDK updateUser) is reflected in
 * the claims Firebase checks during MFA enrollment.
 * @returns The refreshed emailVerified flag.
 */
export const refreshEmailVerified = async (): Promise<boolean> => {
  const user = auth.currentUser;
  if (!user) return false;
  await user.reload();
  await user.getIdToken(true);
  return auth.currentUser?.emailVerified ?? false;
};

/**
 * Re-authenticates a password user; required when enrollment hits
 * auth/requires-recent-login.
 */
export const reauthenticateWithPassword = async (
  password: string,
): Promise<void> => {
  const user = auth.currentUser;
  if (!user?.email) throw new Error("No user is currently logged in");
  const credential = EmailAuthProvider.credential(user.email, password);
  await reauthenticateWithCredential(user, credential);
};

/**
 * Re-authenticates a Google user via popup; required when enrollment hits
 * auth/requires-recent-login.
 */
export const reauthenticateWithGoogle = async (): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error("No user is currently logged in");
  await reauthenticateWithPopup(user, new GoogleAuthProvider());
};

/**
 * Extracts the multi-factor resolver from an auth/multi-factor-auth-required
 * sign-in error. The resolver carries the pending sign-in session.
 */
export const getMfaResolver = (error: unknown): MultiFactorResolver =>
  getMultiFactorResolver(auth, error as MultiFactorError);

/**
 * Completes a sign-in that was interrupted by the MFA challenge, using the
 * 6-digit code from the user's authenticator app.
 */
export const resolveTotpSignIn = async (
  resolver: MultiFactorResolver,
  code: string,
): Promise<UserCredential> => {
  const totpHint = resolver.hints.find(
    (hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID,
  );
  if (!totpHint) {
    throw new Error("No authenticator app is enrolled for this account");
  }
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(
    totpHint.uid,
    code,
  );
  return resolver.resolveSignIn(assertion);
};

/**
 * Which factor types the pending MFA challenge can be satisfied with.
 */
export const getResolverFactors = (
  resolver: MultiFactorResolver,
): { hasTotp: boolean; hasSms: boolean; smsPhoneHint: string } => {
  const smsHint = resolver.hints.find(
    (hint) => hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID,
  ) as PhoneMultiFactorInfo | undefined;
  return {
    hasTotp: resolver.hints.some(
      (hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID,
    ),
    hasSms: !!smsHint,
    // Firebase pre-masks this (e.g. "+*******202").
    smsPhoneHint: smsHint?.phoneNumber ?? "",
  };
};

/**
 * Sends the challenge SMS for a sign-in interrupted by MFA. Returns the
 * verificationId that resolveSmsSignIn needs.
 */
export const startSmsChallenge = async (
  resolver: MultiFactorResolver,
  recaptchaVerifier: RecaptchaVerifier,
): Promise<string> => {
  const smsHint = resolver.hints.find(
    (hint) => hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID,
  );
  if (!smsHint) throw new Error("No SMS factor is enrolled for this account");
  return new PhoneAuthProvider(auth).verifyPhoneNumber(
    { multiFactorHint: smsHint, session: resolver.session },
    recaptchaVerifier,
  );
};

/**
 * Completes a sign-in using the SMS code from startSmsChallenge.
 */
export const resolveSmsSignIn = async (
  resolver: MultiFactorResolver,
  verificationId: string,
  code: string,
): Promise<UserCredential> => {
  const credential = PhoneAuthProvider.credential(verificationId, code);
  const assertion = PhoneMultiFactorGenerator.assertion(credential);
  return resolver.resolveSignIn(assertion);
};
